<template>
    <q-tab-panel name="EventStartListTab">
        <div v-if="editMode === EditMode.Display">
            <q-tabs
                v-model="selClass"
                align="left"
                dense
                no-caps
                narrow-indicator
                active-color="secondary"
                v-if="moreClasses"
            >
                <q-tab :ripple="false" name="-all" :label="ts('allClasses')" />
                <q-tab
                    :ripple="false"
                    :name="boatClass.boatClassId"
                    :label="boatClass.eventClassName ?? ''"
                    v-for="boatClass in attendedClasses"
                    :key="boatClass.id"
                />
            </q-tabs>

            <qe-form>
                <div class="text-right filter-bar" v-if="allowOnlyCaptainsOption">
                    <q-toggle
                        v-model="showOnlyCaptains"
                        color="secondary"
                        :label="showOnlyCaptains ? ts('showOnlyCaptains') : ts('showCrewOnBoats')"
                        @update:model-value="saveShowOnlyCaptains"
                    />
                </div>

                <q-table
                    flat
                    :rows="attendedBoats"
                    :expanded="boatsExpanded"
                    :columns="boatsColumns"
                    :visible-columns="boatsVisibleColumns"
                    row-key="id"
                    binary-state-sort
                    :rows-per-page-options="[0, 10, 20, 30, 40, 50]"
                    class="q-pt-md"
                >
                    <template v-slot:body="props">
                        <q-tr
                            :props="props"
                            :class="{ 'invalid': !props.row.isValid, 'current-boat': props.expand, 'license-issue': props.row.isLicenseIssue }"
                        >
                            <q-tooltip v-if="props.row.isLicenseIssue">
                                {{ ts('licenseIssueOnBoat') }}
                            </q-tooltip>
                            <q-td v-for="col in props.cols" :key="col.name" :props="props">
                                <div v-if="col.name === 'actions'">
                                    <q-btn
                                        flat
                                        :icon="props.expand ? 'person_remove' : 'person_add'"
                                        color="secondary"
                                        class="qe-ignore-focus"
                                        :title="props.expand ? ts('closeCrewList') : ts('openCrewList')"
                                        @click="toggleExpanded(props.row.id)"
                                    />
                                    <q-btn
                                        flat
                                        icon="delete"
                                        color="negative"
                                        class="qe-ignore-focus"
                                        :title="ts('removeBoat')"
                                        @click="removeBoat(props.row)"
                                    />
                                </div>
                                <div v-else-if="col.name === 'category'" class="minicrewlist">
                                    <div v-for="category in props.row.orderedCategories" :key="category.id">
                                        {{ category.visibleShortcut }}
                                    </div>
                                </div>
                                <div v-else-if="col.name === 'crew'" class="minicrewlist">
                                    <div v-if="showOnlyCaptains" class="captain">
                                        {{
                                            props.row.orderedCrewList.length > 0 ? props.row.orderedCrewList[0].reverseFullName : ''
                                        }}
                                    </div>
                                    <div
                                        v-for="crew in props.row.orderedCrewList"
                                        :key="crew.id"
                                        :class="crew.captain ? 'captain' : ''"
                                        v-else
                                    >
                                        <q-icon
                                            :name="crew.sex === 'male' ? 'man' : 'woman'"
                                            :color="crew.sex === 'male' ? 'blue' : 'red'"
                                            size="1.5em"
                                        />
                                        {{ crew.reverseFullName }}
                                    </div>
                                </div>
                                <div
                                    v-else-if="col.name === 'handicap' && props.row.eventClass?.handicapSystem !== null"
                                    :class="{ 'cursor-pointer': true, 'text-right': props.row.eventClass?.isHandicapByValue }"
                                >
                                    {{ props.row.handicapShortcut }}
                                    <q-popup-edit
                                        v-model="props.row.handicapDefaultSelected"
                                        :title="ts('handicap')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateBoatHandicap(props.row, val, origVal)"
                                        v-if="props.row.eventClass?.isHandicapSelectable"
                                    >
                                        <q-select
                                            v-model="scope.value"
                                            dense
                                            autofocus
                                            :options="allHandicaps(props.row.eventClass?.handicapSystem)"
                                            option-value="label"
                                            option-label="label"
                                            map-options
                                            emit-value
                                            @keyup.enter="scope.set"
                                        />
                                    </q-popup-edit>
                                    <q-popup-edit
                                        v-model="props.row.handicapDefaultValue"
                                        :title="ts('handicap')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateBoatHandicap(props.row, val, origVal)"
                                        v-else-if="props.row.eventClass?.isHandicapByValue"
                                    >
                                        <q-input
                                            v-model.number="scope.value"
                                            dense
                                            autofocus
                                            type="number"
                                            @keyup.enter="scope.set"
                                        />
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'boatClass'" class="cursor-pointer">
                                    {{ props.row.eventClass?.eventClassName }}
                                    <q-popup-edit
                                        v-model="props.row.boatClassId"
                                        :title="ts('boatClass')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateBoatClass(props.row, val, origVal)"
                                    >
                                        <q-select
                                            v-model="scope.value"
                                            dense
                                            :options="boatClassList"
                                            autofocus
                                            option-value="value"
                                            option-label="label"
                                            map-options
                                            emit-value
                                            @keyup.enter="scope.set"
                                        ></q-select>
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'sailNo'" class="cursor-pointer">
                                    <span :class="'fi ' + 'fi-' + props.row.sailNoFlag" />
                                    {{ props.row.sailNo }}
                                    <q-popup-edit
                                        v-model="props.row.sailNumber"
                                        :title="ts('editSailNo')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateSailNumber(props.row, val, origVal)"
                                    >
                                        <q-input
                                            type="number"
                                            v-model.number="scope.value"
                                            dense
                                            autofocus
                                            @keyup.enter="scope.set"
                                        />
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'boatName'" class="cursor-pointer">
                                    {{ props.row.name }}
                                    <q-icon
                                        name="more_horiz"
                                        color="secondary"
                                        v-if="props.row.boatClass?.useBoatNames
                                        && (props.row.name === undefined || props.row.name === null || props.row.name.length === 0)"
                                    />
                                    <q-popup-edit
                                        v-model="props.row.name"
                                        :title="ts('editBoatName')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateSailName(props.row, val, origVal)"
                                    >
                                        <q-input v-model="scope.value" dense autofocus @keyup.enter="scope.set" />
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'paid'">
                                    <q-icon
                                        name="savings"
                                        color="positive"
                                        size="1.75em"
                                        :title="ts('entryFeePaidManually')"
                                        v-if="props.row.paid === 2"
                                    />
                                    <q-icon
                                        name="credit_score"
                                        color="positive"
                                        size="1.75em"
                                        :title="ts('entryFeePaid')"
                                        v-else-if="props.row.paid === 1"
                                    />
                                    <q-btn
                                        flat
                                        icon="credit_card_off"
                                        color="negative"
                                        :title="ts('entryFeeUnpaid')"
                                        v-else
                                        @click="feePaid(props.row)"
                                    />
                                </div>
                                <div v-else-if="col.name === 'detail'" class="cursor-pointer">
                                    {{ props.row.detail }}
                                    <q-icon
                                        name="more_horiz"
                                        color="secondary"
                                        v-if="props.row.detail === undefined || props.row.detail === null || props.row.detail.length === 0"
                                    />
                                    <q-popup-edit
                                        v-model="props.row.detail"
                                        :title="ts('editNote')"
                                        auto-save
                                        v-slot="scope"
                                        buttons
                                        @save="(val, origVal) => updateDetail(props.row, val, origVal)"
                                    >
                                        <q-input v-model="scope.value" dense autofocus @keyup.enter="scope.set" />
                                    </q-popup-edit>
                                </div>
                                <span v-else>{{ col.value }}</span>
                            </q-td>
                        </q-tr>
                        <q-tr v-show="props.expand" :props="props" class="current-boat">
                            <q-td colspan="100%">
                                <q-table
                                    flat
                                    :rows="props.row.orderedCrewList"
                                    :columns="crewColumns"
                                    row-key="id"
                                    class="q-pa-md q-ml-xl q-mr-xl"
                                    v-draggable-table="{
                                        options: {
                                            mode: props.row.orderedCrewList.length > 2 ? 'row' : 'none',
                                            onlyBody: true,
                                            dragHandler: 'tr.draggable',
                                            dragula: { accepts: reorderOnAccept },
                                        },
                                        onDrop: reorderCrew,
                                    }"
                                    v-model:pagination="pagination"
                                    :rows-per-page-options="[0]"
                                >
                                    <template v-slot:body="props">
                                        <q-tr
                                            :props="props"
                                            :class="{ 'invalid': !props.row.isValid, 'draggable': !props.row.captain, 'license-issue': props.row.isLicenseIssue }"
                                        >
                                            <q-tooltip v-if="props.row.isLicenseIssue">
                                                {{ ts('licenseIssue') }}
                                            </q-tooltip>
                                            <q-td v-for="col in props.cols" :key="col.name" :props="props">
                                                <q-icon
                                                    v-if="col.name === 'sex'"
                                                    :name="props.row.sex === 'male' ? 'man' : 'woman'"
                                                    :color="props.row.sex === 'male' ? 'blue' : 'red'"
                                                    size="1.8em"
                                                />
                                                <q-icon
                                                    v-else-if="col.name === 'captain' && props.row.captain"
                                                    name="star"
                                                    color="orange"
                                                    size="1.8em"
                                                />
                                                <span v-else-if="col.name === 'actions'">
                                                    <q-btn
                                                        v-if="props.row.canImproveToPublic"
                                                        flat
                                                        icon="self_improvement"
                                                        color="grey"
                                                        class="qe-ignore-focus"
                                                        :title="ts('makePublic')"
                                                        @click="makePublic(props.row)"
                                                    />
                                                    <q-btn
                                                        v-if="props.row.canBeCaptain"
                                                        flat
                                                        icon="star"
                                                        color="secondary"
                                                        class="qe-ignore-focus"
                                                        :title="ts('promoteCaptain')"
                                                        @click="promoteCaptain(props.row)"
                                                    />
                                                    <q-btn flat icon="star" disable class="invisible-button" v-else />
                                                    <q-btn
                                                        flat
                                                        icon="phone"
                                                        color="secondary"
                                                        class="qe-ignore-focus"
                                                        :title="ts('crewContact')"
                                                        @click="crewContact(props.row)"
                                                    />
                                                    <q-btn
                                                        flat
                                                        icon="delete"
                                                        color="negative"
                                                        class="qe-ignore-focus"
                                                        :title="ts('removeCrew')"
                                                        @click="removeCrew(props.row)"
                                                    />
                                                </span>
                                                <span v-else-if="col.name === 'note'" class="cursor-pointer">
                                                    {{ col.value }}
                                                    <q-icon
                                                        name="more_horiz"
                                                        color="secondary"
                                                        v-if="props.row.note === undefined || props.row.note === null || props.row.note.length === 0"
                                                    />
                                                    <q-popup-edit
                                                        v-model="props.row.note"
                                                        :title="ts('editNote')"
                                                        auto-save
                                                        v-slot="scope"
                                                        buttons
                                                        @save="(val, origVal) => updateCrewNote(props.row, val, origVal)"
                                                    >
                                                        <q-input
                                                            v-model="scope.value"
                                                            dense
                                                            autofocus
                                                            @keyup.enter="scope.set"
                                                        />
                                                    </q-popup-edit>
                                                </span>
                                                <span
                                                    v-else-if="col.name === 'birthYear'"
                                                    class="cursor-pointer"
                                                    :title="props.row.agedBirthYear == null || props.row.birthYear === undefined
                                                    ? ts('agedBirthYearEditTooltip')
                                                    : ts(props.row.sex === 'female' ? 'agedBirthYearTooltipFemale' : 'agedBirthYearTooltip', {
                                                        birthYear: props.row.birthYear,
                                                        agedBirthYear: props.row.agedBirthYear,
                                                    })"
                                                >
                                                    {{ props.row.effectiveBirthYear }}<sup
                                                        v-if="props.row.agedBirthYear != null"
                                                        class="aged-birth-year-marker"
                                                    >*</sup>
                                                    <q-popup-edit
                                                        v-model="props.row.agedBirthYear"
                                                        :title="ts('agedBirthYear')"
                                                        v-slot="scope"
                                                        buttons
                                                        @save="(val, origVal) => updateAgedBirthYear(props.row, val, origVal)"
                                                    >
                                                        <q-input
                                                            v-model="scope.value"
                                                            type="number"
                                                            dense
                                                            autofocus
                                                            clearable
                                                            @keyup.enter="scope.set"
                                                        />
                                                    </q-popup-edit>
                                                </span>
                                                <span v-else>{{ col.value }}</span>
                                            </q-td>
                                        </q-tr>
                                    </template>
                                    <template #no-data>
                                        <div>{{ ts('noCrew') }}</div>
                                    </template>
                                    <template v-slot:bottom-row>
                                        <q-td colspan="4" class="new-crew-member">
                                            <qe-combo-box
                                                v-model="newCrewMember"
                                                ref="newCrewMemberField"
                                                :label="ts('newRegisteredMember')"
                                                :options="crewList"
                                                @filter="crewFilter"
                                                @keydown.enter="chooseOnlyOneMember"
                                                @keydown.tab="chooseOnlyOneMember"
                                            >
                                                <template v-slot:option="scope">
                                                    <q-item v-bind="scope.itemProps">
                                                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                                                    </q-item>
                                                </template>
                                            </qe-combo-box>
                                        </q-td>
                                        <q-td class="text-center" colspan="3">
                                            <qe-btn
                                                :label="ts('add')"
                                                color="secondary"
                                                @click="addNewCrew(props.row)"
                                            />
                                            <qe-btn
                                                class="q-ml-xl"
                                                :label="ts('addForeignCrew')"
                                                color="secondary"
                                                @click="addNewForeignCrew(props.row)"
                                            />
                                            <qe-btn
                                                class="q-ml-sm"
                                                :label="ts('addOneTimeCrew')"
                                                color="secondary"
                                                @click="addNewOneTimeCrew(props.row)"
                                            />
                                            <qe-btn
                                                class="q-ml-sm"
                                                v-if="props.row.canAddPublicCrew"
                                                :label="ts('addPublicCrew')"
                                                color="secondary"
                                                @click="addNewPublicCrew(props.row)"
                                            />
                                        </q-td>
                                    </template>
                                </q-table>
                            </q-td>
                        </q-tr>
                    </template>
                    <template #no-data>
                        <div>{{ ts('noBoats') }}</div>
                    </template>
                    <template v-slot:bottom-row>
                        <q-td v-if="selClass === '-all'">
                            <qe-combo-box
                                v-model="newBoatClass"
                                ref="newBoatClassField"
                                :label="ts('boatClass')"
                                :options="boatClassList"
                                autofocus
                                class="boat-class-column"
                                @filter="boatClassFilter"
                                @keydown.enter="chooseOnlyOneBoatClass"
                                @keydown.tab="chooseOnlyOneBoatClass"
                            >
                                <template v-slot:option="scope">
                                    <q-item v-bind="scope.itemProps">
                                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                                    </q-item>
                                </template>
                            </qe-combo-box>
                        </q-td>
                        <q-td class="row inline no-wrap">
                            <qe-combo-box
                                v-model="newBoatCountry"
                                ref="newBoatCountryField"
                                :label="ts('country')"
                                :options="countryList"
                                option-label="value"
                                class="q-mr-md boat-country-column"
                                @filter="countryFilter"
                                @keydown.enter="chooseOnlyOneCountry"
                                @keydown.tab="chooseOnlyOneCountry"
                            >
                                <template v-slot:option="scope">
                                    <q-item v-bind="scope.itemProps">
                                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                                    </q-item>
                                </template>
                            </qe-combo-box>
                            <qe-input
                                type="number"
                                v-model.number="newBoatSailNo"
                                :label="ts('sailNo')"
                                class="boat-sailno-column"
                            ></qe-input>
                        </q-td>
                        <q-td v-if="currentBoatClassUseNames">
                            <qe-input v-model="newBoatName"></qe-input>
                        </q-td>
                        <q-td>
                            <qe-input v-model="newBoatDetail"></qe-input>
                        </q-td>
                        <q-td class="text-center">
                            <qe-btn
                                :label="ts('addBoat')"
                                color="secondary"
                                @click="addNewBoat()"
                            />
                        </q-td>
                    </template>
                </q-table>
            </qe-form>

            <div class="row justify-between q-mt-lg">
                <q-btn :label="ts('updateRegisteredFromServer')" outline color="secondary" @click="updateFromServer" />
                <q-btn
                    :label="ts('sendToRrs')"
                    outline
                    color="secondary"
                    @click="sendToRrs"
                    v-if="(selClass === '-all' || !moreClasses.value) && event.rrsUuid !== null && attendedBoats.length > 0"
                />
                <q-btn
                    :label="ts('printStartList')"
                    outline
                    color="secondary"
                    @click="printStartList(true)"
                    v-if="selClass !== '-all' && attendedBoats.length > 0 && allowOnlyCaptainsOption && showOnlyCaptains"
                />
                <q-btn
                    :label="ts('printStartList')"
                    outline
                    color="secondary"
                    @click="printStartList(false)"
                    v-else-if="selClass !== '-all' && attendedBoats.length > 0 && !allowOnlyCaptainsOption"
                />
                <q-btn-dropdown
                    outline
                    color="secondary"
                    :label="ts('printStartList')"
                    v-else-if="selClass !== '-all' && attendedBoats.length > 0"
                >
                    <q-list>
                        <q-item clickable v-close-popup @click="printStartList(false)">
                            <q-item-section>
                                {{ ts('withCrew') }}
                            </q-item-section>
                        </q-item>
                        <q-item clickable v-close-popup @click="printStartList(true)">
                            <q-item-section>
                                {{ ts('onlyCaptains') }}
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
            </div>

            <div class="row justify-between q-mt-lg">
                <q-btn
                    :label="ts('batchHandicapSetup')"
                    outline
                    color="secondary"
                    @click="batchHandicapSetup"
                    v-if="selClass !== '-all' && isHandicapUsed"
                />
                <q-btn
                    :label="ts('printPresenceList')"
                    outline
                    color="secondary"
                    @click="printStartList(true, true)"
                    v-if="selClass !== '-all' && attendedBoats.length > 0 && allowOnlyCaptainsOption && showOnlyCaptains"
                />
                <q-btn
                    :label="ts('printPresenceList')"
                    outline
                    color="secondary"
                    @click="printStartList(false, true)"
                    v-else-if="selClass !== '-all' && attendedBoats.length > 0 && !allowOnlyCaptainsOption"
                />
                <q-btn-dropdown
                    outline
                    color="secondary"
                    :label="ts('printPresenceList')"
                    v-else-if="selClass !== '-all' && attendedBoats.length > 0"
                >
                    <q-list>
                        <q-item clickable v-close-popup @click="printStartList(false, true)">
                            <q-item-section>
                                {{ ts('withCrew') }}
                            </q-item-section>
                        </q-item>
                        <q-item clickable v-close-popup @click="printStartList(true, true)">
                            <q-item-section>
                                {{ ts('onlyCaptains') }}
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
            </div>
        </div>

        <div v-else-if="editMode === EditMode.BatchHandicapSetup">
            <q-table
                flat
                dense
                square
                :rows="attendedBoats"
                :columns="handicapColumns"
                row-key="id"
                :pagination="{ rowsPerPage: 0 }"
            >
                <template v-slot:body-cell="props">
                    <q-td :props="props">
                        <template v-if="typeof props.value === 'object' && props.value !== null">
                            <div
                                :class="{ 'cursor-pointer': props.col.name === 'sailNo' || props.col.name.startsWith('race') }"
                            >
                                {{ props.value.value }}
                                <q-tooltip v-if="props.value.coef !== null">
                                    {{ props.value.coef.toFixed(3) }}
                                </q-tooltip>
                            </div>

                            <template v-if="props.col.name.startsWith('race')">
                                <q-popup-edit
                                    :model-value="props.value.isR1 ? props.value.value : (props.value.originalValue ?? '--')"
                                    :title="ts('handicap')"
                                    v-slot="scope"
                                    buttons
                                    @save="(val, origVal) =>
                                    props.value.isR1
                                        ? updateBoatHandicap(props.row, val, origVal)
                                        : updateRaceHandicap(props.row, props.value.raceId, val, origVal)"
                                    v-if="props.row.eventClass?.isHandicapSelectable"
                                >
                                    <q-select
                                        v-model="scope.value"
                                        dense
                                        autofocus
                                        :options="props.value.isR1
                                        ? allHandicaps(props.row.eventClass?.handicapSystem)
                                        : [{ label: '-- ' + ts('noChange') + ' --', value: '--' }, ...allHandicaps(props.row.eventClass?.handicapSystem)]"
                                        map-options
                                        option-value="label"
                                        option-label="label"
                                        emit-value
                                        @keyup.enter="scope.set"
                                    />
                                </q-popup-edit>
                                <q-popup-edit
                                    :model-value="props.value.isR1 ? props.value.value : (props.value.originalValue ?? '--')"
                                    :title="ts('handicap')"
                                    v-slot="scope"
                                    buttons
                                    @save="(val, origVal) =>
                                    props.value.isR1
                                        ? updateBoatHandicap(props.row, val, origVal)
                                        : updateRaceHandicap(props.row, props.value.raceId, val, origVal)"
                                    v-else-if="props.row.eventClass?.isHandicapByValue"
                                >
                                    <div v-if="!props.value.isR1" class="q-mb-sm">
                                        <!-- dprint-ignore -->
                                        <q-btn flat dense size="sm" color="primary" :label="'-- ' + ts( 'noChange' ) + ' --'" @click="scope.value = '--'; scope.set()" />
                                    </div>
                                    <q-input
                                        v-if="scope.value === '--'"
                                        :model-value="'-- ' + ts('noChange') + ' --'"
                                        readonly
                                        dense
                                        autofocus
                                    />
                                    <q-input
                                        v-else
                                        v-model.number="scope.value"
                                        dense
                                        autofocus
                                        type="number"
                                        @keyup.enter="scope.set"
                                    />
                                </q-popup-edit>
                            </template>
                        </template>
                        <template v-else>
                            {{ props.value }}
                        </template>
                    </q-td>
                </template>
            </q-table>
            <div class="row justify-end q-mt-lg">
                <q-btn :label="ts('close')" outline color="primary" @click="editMode = EditMode.Display" />
            </div>
        </div>
    </q-tab-panel>
</template>

<script setup lang="ts">
import { decodeBoatClass } from '@/network/decoders'
import apiRequest from '@/network/httpClient'
import { allHandicaps } from '@/orm/catalogs/Handicaps'
import { localization } from '@/plugins/i18n'
import Confirmation from '@dialogs/Confirmation.vue'
import CrewContact from '@dialogs/CrewContact.vue'
import ForeignCrewRegistration from '@dialogs/ForeignCrewRegistration.vue'
import InfoBox from '@dialogs/InfoBox.vue'
import OneTimeCrewRegistration from '@dialogs/OneTimeCrewRegistration.vue'
import PublicCrewRegistration from '@dialogs/PublicCrewRegistration.vue'
import UpdateRegistrations from '@dialogs/UpdateRegistrations.vue'
import Country from '@model/Country'
import EventResult from '@model/EventResult'
import { Defaults, Options } from '@model/Setting'
import QeBtn from '@qe/qeBtn.vue'
import QeComboBox from '@qe/qeComboBox.vue'
import QeForm from '@qe/qeForm.vue'
import QeInput from '@qe/qeInput.vue'
import EventBoat from '@model/EventBoat'
import EventBoatRace from '@model/EventBoatRace'
import EventCrew, { crewDump, crewHash } from '@model/EventCrew'
import BoatClassesRepo from '@repo/BoatClassesRepo'
import CountriesRepo from '@repo/CountriesRepo'
import EventsRepo from '@repo/EventsRepo'
import MembersRepo from '@repo/MembersRepo'
import { DateFormat, ds, ns, tcs, ts } from '@/plugins/i18nFormat'
import nvl from '@/utils/nvl'
import SettingsRepo from '@repo/SettingsRepo'
import axios from 'axios'
import destr from 'destr'
import { isPositiveInteger } from 'is-what'
import { useRepo } from 'pinia-orm'
import { QSelect, useQuasar } from 'quasar'
import { rodnecislo } from 'rodnecislo'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { sprintf } from 'sprintf-js'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { buildPrintMeta, buildStandardHeader } from '@/plugins/printReports'

pdfMake.addVirtualFileSystem(pdfFonts)

const quasar = useQuasar()
const route = useRoute()

const props = defineProps<{ event: any }>()
const event = computed(() => (props.event ?? useRepo(EventsRepo).getEvent(String(route.params.eventId), true)))

const attendedClasses = computed(() => (event.value.orderedClasses))
const moreClasses = computed(() => (attendedClasses.value.length > 1))

const selClass = ref(event.value.orderedClasses.length > 1 ? '-all' : event.value.orderedClasses[0].boatClassId)

const showOnlyCaptains = ref(event.value.showOnlyCaptains)
const boatsExpanded = ref([])

const countriesRepo = computed(() => (useRepo(CountriesRepo)))
const membersRepo = computed(() => (useRepo(MembersRepo)))

const attendedBoats = computed(() => (event.value.orderedBoats(selClass.value === '-all' ? null : selClass.value)))

const maxCrewOnClass = computed(() => (selClass.value === '-all'
    ? 0
    : (attendedClasses.value.filter(eventClass => eventClass.boatClassId === selClass.value)[0]?.maxCrew ?? 0))
)

const isBoatNamesUsed = computed(() => (selClass.value === '-all'
    ? false
    : (attendedClasses.value.filter(eventClass => eventClass.boatClassId === selClass.value)[0]?.isBoatNamesUsed
        ?? false))
)

const allowOnlyCaptainsOption = computed(() => (maxCrewOnClass.value >= 3))
const isCategoriesUsed = computed(() => (attendedBoats.value.some((boat) => boat.categories.length > 0)))

const isHandicapUsed = computed(() => (selClass.value === '-all'
    ? attendedClasses.value.some(eventClass => eventClass.handicapSystem !== null)
    : attendedClasses.value.filter(eventClass => eventClass.boatClassId === selClass.value)[0]?.handicapSystem !== null)
)

const boatClassList = ref(event.value.orderedClassesList)
const countryList = ref(countriesRepo.value.countryForSailList())
const crewList = ref(membersRepo.value.orderedList())

const wsIdRequired = computed(() => (event.value.wsEventId !== null && event.value.wsEventId.length > 0))

const pagination = ref()

const newBoatClass = ref()
const newBoatCountry = ref('CZE')
const newBoatSailNo = ref()
const newBoatName = ref()
const newBoatDetail = ref()
const newCrewMember = ref()

const newBoatCountryField = ref()
const newBoatClassField = ref()

const newCrewMemberField = ref()

const boatsColumns = ref([
    {
        name: 'boatClass',
        label: ts('boatClass'),
        align: 'left',
        field: row => row.eventClassName ?? '',
        sortable: true,
    },
    {
        name: 'sailNo',
        required: true,
        label: ts('sailNo'),
        align: 'left',
        field: row => row.sailNo,
        sortable: true,
    },
    {
        name: 'boatName',
        label: ts('boatName'),
        align: 'left',
        field: row => nvl(row.name, ''),
        sortable: true,
    },
    {
        name: 'category',
        label: ts('category'),
        align: 'left',
        field: () => '',
        sortable: false,
    },
    {
        name: 'detail',
        label: event.value.descInResultsTitle ?? ts('note'),
        align: 'left',
        field: row => nvl(row.detail, ''),
        sortable: true,
    },
    {
        name: 'crew',
        required: true,
        label: ts('crew'),
        align: 'left',
        field: () => '',
        sortable: false,
    },
    {
        name: 'handicap',
        required: false,
        label: ts('handicap'),
        align: 'left',
        field: () => '',
        sortable: false,
    },
    {
        name: 'paid',
        required: true,
        label: ts('fee'),
        align: 'center',
        field: row => row.paid > 0,
        storable: false,
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
    },
])

const crewColumns = ref([
    {
        name: 'regId',
        label: ts('regId'),
        align: 'left',
        field: row => row?.regId,
    },
    ...(wsIdRequired.value
        ? [
            {
                name: 'wsId',
                label: ts('wsId'),
                align: 'left',
                field: row => row?.wsId,
            },
        ]
        : []),
    {
        name: 'captain',
        label: ts('captain'),
        align: 'center',
        field: () => '',
    },
    {
        name: 'sex',
        label: ts('sex'),
        align: 'center',
        field: row => row?.sex,
    },
    {
        name: 'fullName',
        label: ts('fullName'),
        align: 'left',
        field: row => row?.reverseFullName,
    },
    {
        name: 'birthYear',
        label: ts('birthYear'),
        align: 'left',
        field: row => row?.effectiveBirthYear,
    },
    {
        name: 'note',
        label: event.value.crewDescInResultsTitle ?? ts('note'),
        align: 'left',
        field: row => row?.note,
    },
    {
        name: 'club',
        label: ts('club'),
        align: 'left',
        field: row => row?.visibleClub,
    },
    {
        name: 'actions',
        label: ts('actions'),
        align: 'right',
        field: () => '',
    },
])

const handicapColumns = computed(() => {
    const cols = [
        {
            name: 'sailNo',
            label: ts('sailNo'),
            align: 'left',
            field: row => {
                const handicapSystem = row.eventClass?.handicapSystem
                let coef = null
                const value = row?.sailNo

                if (value && handicapSystem) {
                    coef = row.calcHandicapCoef()
                }

                return { value, coef }
            },
            sortable: true,
        },
        {
            name: 'captain',
            label: ts('captain'),
            align: 'left',
            field: row => ({ value: row?.captain?.reverseFullName ?? '', coef: null }),
            sortable: true,
        },
    ]

    const raceCount = event.value.races.length

    for (let i = 1; i <= raceCount; i++) {
        const race = event.value.races.find(r => r.race === i)
        cols.push({
            name: 'race' + i,
            label: 'R' + i,
            align: 'center',
            field: row => {
                const prevRaces = event.value.races.filter(r => r.race < i).sort((a, b) => b.race - a.race)
                let prevValue = row.handicapShortcut

                for (const prevRace of prevRaces) {
                    const br = row.eventBoatRaces?.find(r => r.eventRaceId === prevRace.id)
                    const val = br ? (br.handicapValue ?? br.handicapSelected ?? null) : null
                    if (val !== null) {
                        prevValue = val
                        break
                    }
                }

                const br = row.eventBoatRaces?.find(r => r.eventRaceId === (race?.id ?? null))
                const currentValue = br ? (br.handicapValue ?? br.handicapSelected ?? null) : null

                const handicapSystem = row.eventClass?.handicapSystem
                let coef = null
                const effectiveValue = (i === 1)
                    ? (currentValue ?? prevValue)
                    : (currentValue !== null ? currentValue : null)

                if (effectiveValue !== null && effectiveValue !== '...' && handicapSystem) {
                    const isSelectable = row.eventClass?.isHandicapSelectable
                    if (isSelectable) {
                        const handicaps = allHandicaps(handicapSystem)?.filter(h =>
                            h.value === Number(effectiveValue) || h.label === String(effectiveValue)
                        )
                        if (handicaps && handicaps.length >= 1) {
                            coef = 1000 / handicaps[0].value
                        }
                    } else {
                        const val = parseFloat(effectiveValue)
                        if (!Number.isNaN(val)) {
                            if (handicapSystem === 'pynumber') coef = 1000 / val
                            else if (handicapSystem === 'texel') coef = 100 / val
                            else if (handicapSystem === 'zadar') coef = val
                        }
                    }
                }

                if (i === 1) {
                    return {
                        value: currentValue ?? prevValue,
                        coef,
                        raceId: race?.id ?? null,
                        isR1: true,
                        originalValue: currentValue,
                    }
                }

                const isR1 = (i === 1) || (event.value.races.length === 0 && race?.id === undefined)
                let displayValue = (currentValue !== null && currentValue !== prevValue)
                    ? currentValue
                    : (isR1 ? prevValue : '...')
                if (displayValue !== '...' && row.eventClass?.isHandicapSelectable) {
                    const handicaps = allHandicaps(handicapSystem)?.filter(h =>
                        h.value === Number(displayValue) || h.label === String(displayValue)
                    )
                    if (handicaps && handicaps.length >= 1) {
                        displayValue = handicaps[0].label
                    }
                }

                return { value: displayValue, coef, raceId: race?.id ?? null, isR1, originalValue: currentValue }
            },
            sortable: false,
        })
    }

    cols.push({
        name: 'raceNext',
        label: 'R' + (raceCount + 1),
        align: 'center',
        field: row => {
            const br = row.eventBoatRaces?.find(r => r.eventRaceId === null)
            const currentValue = br ? (br.handicapValue ?? br.handicapSelected ?? null) : null

            const handicapSystem = row.eventClass?.handicapSystem
            let coef = null

            if (currentValue !== null && handicapSystem) {
                const isSelectable = row.eventClass?.isHandicapSelectable
                if (isSelectable) {
                    const handicaps = allHandicaps(handicapSystem)?.filter(h =>
                        h.value === Number(currentValue) || h.label === String(currentValue)
                    )
                    if (handicaps && handicaps.length >= 1) {
                        coef = 1000 / handicaps[0].value
                    }
                } else {
                    const val = parseFloat(currentValue)
                    if (!Number.isNaN(val)) {
                        if (handicapSystem === 'pynumber') coef = 1000 / val
                        else if (handicapSystem === 'texel') coef = 100 / val
                        else if (handicapSystem === 'zadar') coef = val
                    }
                }
            }

            const isR1 = event.value.races.length === 0
            let displayValue = currentValue ?? (isR1 ? row.handicapShortcut : '...')
            if (displayValue !== '...' && row.eventClass?.isHandicapSelectable) {
                const handicaps = allHandicaps(handicapSystem)?.filter(h =>
                    h.value === Number(displayValue) || h.label === String(displayValue)
                )
                if (handicaps && handicaps.length >= 1) {
                    displayValue = handicaps[0].label
                }
            }

            return { value: displayValue, coef, raceId: null, isR1, originalValue: currentValue }
        },
        sortable: false,
    })

    return cols
})

const currentBoatClassUseNames = computed(
    () => ((selClass.value === '-all'
        && attendedClasses.value.filter(eventClass => eventClass.boatClass?.useBoatNames ?? false).length > 0)
        || (attendedClasses.value.filter(eventClass =>
            eventClass.boatClass?.id ?? eventClass.boatClassId === selClass.value
        )[0]?.boatClass?.useBoatNames ?? false)),
)

const boatsVisibleColumns = computed(() => (boatsColumns.value.map(col => col.name)
    .filter(name => (currentBoatClassUseNames.value || name !== 'boatName'))
    .filter(name => (selClass.value === '-all' ? true : name !== 'boatClass'))
    .filter(name => (isCategoriesUsed.value || name !== 'category'))
    .filter(name => (isHandicapUsed.value || name !== 'handicap')))
)

function reorderOnAccept(el, target, source, sibling) {
    return sibling?.querySelector('.draggable')
}

function countryFilter(val, update) {
    if (val === '') {
        update(() => {
            countryList.value = countriesRepo.value.countryForSailList()
        })
        return
    }

    update(
        () => {
            const needle = val.toSearchable()
            countryList.value = countriesRepo.value.countryForSailList().filter(country =>
                country.label.toSearchable().includes(needle)
            )
        },
        (ref: QSelect) => {
            if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1)
                ref.moveOptionSelection(1, true)
            }
        },
    )
}

function chooseOnlyOneCountry() {
    if (countryList.value != null && countryList.value.length === 1) {
        newBoatCountry.value = countryList.value[0].value
        newBoatCountryField.value.focus()
    }
}

function boatClassFilter(val, update) {
    if (val === '') {
        update(() => {
            boatClassList.value = event.value.orderedClassesList
        })
        return
    }

    update(
        () => {
            const needle = val.toSearchable()
            boatClassList.value = event.value.orderedClassesList.filter(boatClass =>
                boatClass.label.toSearchable().includes(needle)
            )
        },
        (ref: QSelect) => {
            if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1)
                ref.moveOptionSelection(1, true)
            }
        },
    )
}

function chooseOnlyOneBoatClass() {
    if (boatClassList.value != null && boatClassList.value.length === 1) {
        newBoatClass.value = boatClassList.value[0].value
        newBoatClassField.value.focus()
    }
}

function performAddBoat(boatClass) {
    useRepo(EventBoat).save({
        eventId: event.value.id,
        boatClass: boatClass,
        country: newBoatCountry.value,
        sailNumber: String(newBoatSailNo.value),
        name: newBoatName.value,
        detail: newBoatDetail.value,
    })

    event.value?.setModified()

    newBoatClass.value = null
    newBoatCountry.value = 'CZE'
    newBoatSailNo.value = null
    newBoatName.value = null
    newBoatDetail.value = null
}

function addNewBoat() {
    if (
        newBoatCountry.value === undefined || newBoatSailNo.value === undefined || newBoatCountry.value === null
        || newBoatSailNo.value === null
        || newBoatSailNo.value.length === 0 || newBoatCountry.value.length === 0
        || (selClass.value === '-all' && newBoatClass.value == null)
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('missingBoatData'),
            },
        })

        return
    }

    let boatClass = selClass.value === '-all' ? newBoatClass.value : selClass.value

    const eventClass = event.value.getClass(boatClass)

    boatClass = useRepo(BoatClassesRepo).find(boatClass)

    if (boatClass === null)
        return

    if (
        useRepo(EventBoat).where('eventId', event.value.id).where('boatClassId', newBoatClass.value).where(
            'country',
            newBoatCountry.value,
        ).where('sailNumber', String(newBoatSailNo.value)).get().length > 0
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateSailNo', {
                    sailNumber: newBoatCountry.value + ' ' + newBoatSailNo.value,
                    boatClass: eventClass?.eventClassName,
                }),
            },
        })

        return
    }

    if (
        useRepo(EventBoat).where('eventId', event.value.id).where('sailNumber', String(newBoatSailNo.value)).get()
            .length === 0
    ) {
        performAddBoat(boatClass)
        return
    }

    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'warning',
            question: ts('duplicateSailNoWarn', {
                sailNumber: newBoatSailNo.value,
            }),
        },
    })
        .onOk(() => {
            performAddBoat(boatClass)
        })
}

function removeBoat(eventBoat) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeBoatFromEvent', {
                eventTitle: eventBoat.event?.fullTitle,
                boatClass: eventBoat.eventClassName,
                sailNo: eventBoat.sailNo,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventCrew).where('eventBoatId', eventBoat.id).delete()
            useRepo(EventResult).where('eventBoatId', eventBoat.id).delete()
            useRepo(EventBoat).destroy(eventBoat.id)

            event.value?.setModified()
        })
}

function performAddCrew(eventBoat) {
    useRepo(EventCrew).save({
        eventId: event.value.id,
        eventBoatId: eventBoat.id,
        memberId: newCrewMember.value,
        captain: eventBoat.crew.length === 0,
        order: eventBoat.crew.length === 0 ? 1 : Math.max(...eventBoat.crew.map(crew => crew.order)) + 1,
    })

    event.value?.setModified()

    newCrewMember.value = ''
}

function addNewCrew(eventBoat) {
    if (newCrewMember.value === undefined || newCrewMember.value === '' || newCrewMember.value.length === 0) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('missingCrewData'),
            },
        })

        return
    }

    if (useRepo(EventCrew).where('eventBoatId', eventBoat.id).where('memberId', newCrewMember.value).get().length > 0) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateCrewOnBoat', { crew: useRepo(MembersRepo).find(newCrewMember.value)?.fullName }),
            },
        })

        return
    }

    const dup = useRepo(EventCrew).where('memberId', newCrewMember.value).with(
        'eventBoat',
        (query) => (query.with('boatClass').where('eventId', event.value.id)),
    )
        .get().filter((crew) => (crew.eventBoat !== null))
    if (dup.length === 0) {
        performAddCrew(eventBoat)

        return
    }

    const boatClass = dup.map((m) => (m.eventBoat?.boatClass?.name)).filter((c) => (c !== null)).join(', ')

    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'warning',
            question: ts('duplicateCrewOnEvent', {
                crew: useRepo(MembersRepo).find(newCrewMember.value)?.fullName,
                boatClass: boatClass,
            }),
        },
    })
        .onOk(() => {
            performAddCrew(eventBoat)
        })
}

function crewFilter(val, update) {
    if (val === '') {
        update(() => {
            crewList.value = membersRepo.value.orderedList()
        })
        return
    }

    update(
        () => {
            let needle = val.toSearchable()

            if (/^\d{5,8}$/.test(needle))
                needle = needle.substring(0, 4) + '-' + needle.substring(4)

            crewList.value = membersRepo.value.orderedList().filter(member =>
                member.searchLabel.toSearchable().includes(needle)
            )
        },
        (ref: QSelect) => {
            if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1)
                ref.moveOptionSelection(1, true)
            }
        },
    )
}

function chooseOnlyOneMember() {
    if (crewList.value != null && crewList.value.length === 1)
        newCrewMember.value = crewList.value[0].value
}

async function toggleExpanded(val) {
    boatsExpanded.value = boatsExpanded.value[0] === val ? [] : [val]
    newCrewMember.value = undefined

    if (boatsExpanded.value.length > 0) {
        newCrewMemberField.value.focus()
    }
}

function removeCrew(boatCrew) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeCrewFromBoat', { fullName: boatCrew.fullName, sailNo: boatCrew.eventBoat.sailNo }),
        },
    })
        .onOk(() => {
            const eventBoat = useRepo(EventBoat).with('crew', (q) => {
                q.with('nonMemberCitizenshipCountry')
            }).find(boatCrew.eventBoatId)

            if (boatCrew.captain && eventBoat.crew.length >= 2) {
                const candidateCrew = eventBoat.crew.filter(crew => !crew.captain)[0]

                if (candidateCrew)
                    useRepo(EventCrew).save({ id: candidateCrew.id, captain: true })
            }

            useRepo(EventCrew).destroy(boatCrew.id)

            event.value?.setModified()
        })
}

function promoteCaptain(boatCrew) {
    const currentCaptain = useRepo(EventCrew).where('eventBoatId', boatCrew.eventBoatId).where('captain', true).first()

    if (currentCaptain === null)
        useRepo(EventCrew).save({ id: boatCrew.id, captain: true })
    else
        useRepo(EventCrew).save([{ id: currentCaptain.id, captain: false }, { id: boatCrew.id, captain: true }])

    event.value?.setModified()
}

function crewContact(boatCrew) {
    quasar.dialog({
        component: CrewContact,
        componentProps: {
            crew: boatCrew,
            event: event,
        },
    })
}

function updateBoatClass(eventBoat, boatClassId, originalBoatClassId) {
    if (boatClassId === originalBoatClassId)
        return

    const res = event.value.results(originalBoatClassId, null, false).filter(r => r.boat.id === eventBoat.id)

    if (res.length > 0 && res[0].results.length > 0) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('resultExistsForUpdateBoatClass', {
                    sailNumber: eventBoat.sailNo,
                    boatClass: eventBoat.eventClass?.eventClassName,
                }) + ' ' + ts('keepSame'),
            },
        })

        return
    }

    if (
        useRepo(EventBoat).where('eventId', event.value.id).where('boatClassId', boatClassId).where(
            'country',
            eventBoat.country,
        ).where('sailNumber', eventBoat.sailNumber).get().length > 0
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateSailNo', {
                    sailNumber: eventBoat.sailNo,
                    boatClass: event.value.getClass(boatClassId)?.eventClassName,
                }) + ' ' + ts('keepSame'),
            },
        })

        return
    }

    useRepo(EventBoat).save({ id: eventBoat.id, boatClassId: boatClassId })

    event.value?.setModified()
}

function updateSailNumber(eventBoat, sailNumber, originalSailNumber) {
    if (sailNumber === originalSailNumber)
        return

    const newSailNumber = String(sailNumber)

    if (newSailNumber === undefined || newSailNumber === null || newSailNumber.length === 0) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('missingSailNo') + ' ' + ts('keepSame'),
            },
        })

        return
    }

    if (
        useRepo(EventBoat).where('eventId', event.value.id).where('boatClassId', eventBoat.boatClassId).where(
            'country',
            eventBoat.country,
        ).where('sailNumber', newSailNumber).get().length > 0
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateSailNo', {
                    sailNumber: eventBoat.country + ' ' + newSailNumber,
                    boatClass: eventBoat.eventClass?.eventClassName,
                }) + ' ' + ts('keepSame'),
            },
        })

        return
    }

    if (useRepo(EventBoat).where('eventId', event.value.id).where('sailNumber', newSailNumber).get().length === 0) {
        useRepo(EventBoat).save({ id: eventBoat.id, sailNumber: String(sailNumber) })

        event.value?.setModified()

        return
    }

    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'warning',
            question: ts('duplicateSailNoWarnEdit', {
                sailNumber: newSailNumber,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventBoat).save({ id: eventBoat.id, sailNumber: String(sailNumber) })

            event.value?.setModified()
        })
}

function updateSailName(eventBoat, name, originalName) {
    if (name === originalName)
        return

    useRepo(EventBoat).save({ id: eventBoat.id, name: name })

    event.value?.setModified()
}

function updateDetail(eventBoat, detail, originalDetail) {
    if (detail === originalDetail)
        return

    useRepo(EventBoat).save({ id: eventBoat.id, detail: detail })

    event.value?.setModified()
}

function updateCrewNote(eventCrew, note, originalNote) {
    if (note === originalNote)
        return

    useRepo(EventCrew).save({ id: eventCrew.id, note: note })

    event.value?.setModified()
}

function updateAgedBirthYear(
    eventCrew: EventCrew,
    agedBirthYear: number | string | null | undefined,
    originalAgedBirthYear: number | string | null | undefined,
) {
    const normalizedBirthYear = agedBirthYear === null || agedBirthYear === undefined || agedBirthYear === ''
        ? null
        : Number(agedBirthYear)

    if (
        normalizedBirthYear !== null && (!Number.isInteger(normalizedBirthYear) || eventCrew.birthYear === undefined
            || normalizedBirthYear >= eventCrew.birthYear || eventCrew.birthYear - normalizedBirthYear > 100)
    ) {
        useRepo(EventCrew).save({ id: eventCrew.id, agedBirthYear: originalAgedBirthYear ?? null })
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('invalidAgedBirthYear'),
            },
        })

        return
    }

    if (
        normalizedBirthYear
            === (originalAgedBirthYear == null || originalAgedBirthYear === '' ? null : Number(originalAgedBirthYear))
    )
        return

    useRepo(EventCrew).save({ id: eventCrew.id, agedBirthYear: normalizedBirthYear })

    event.value?.setModified()
}

function reorderCrew(from, to) {
    const offset = (pagination.value.page - 1) * pagination.value.rowsPerPage

    if (boatsExpanded.value.length === 0)
        return

    const selectedBoat = boatsExpanded.value[0]
    const eventBoat = useRepo(EventBoat).with('crew', (q) => {
        q.with('nonMemberCitizenshipCountry')
    }).find(selectedBoat)

    const firstCrew = eventBoat.orderedCrewList[offset + from - 1]
    const secondCrew = eventBoat.orderedCrewList[offset + to - 1]

    const firstOrder = firstCrew.order
    const secondOrder = secondCrew.order

    const direction = firstOrder < secondOrder ? 1 : -1

    const allAffected = useRepo(EventCrew).where('eventBoatId', firstCrew.eventBoatId).where('captain', false)
        .where('order', (value) => {
            return direction === 1
                ? (value >= firstOrder && value <= secondOrder)
                : (value <= firstOrder && value >= secondOrder)
        })
        .orderBy('order', direction === 1 ? 'asc' : 'desc').get()

    if (allAffected.length <= 1)
        return

    const firstOne = allAffected.shift()

    let prevOrder = firstOne.order
    let results = []

    allAffected.forEach(function(item) {
        results.push({ id: item.id, order: prevOrder })

        prevOrder = item.order
    })

    results.push({ id: firstOne.id, order: prevOrder })

    useRepo(EventCrew).save(results)

    event.value?.setModified()
}

function addNewPublicCrew(eventBoat) {
    quasar.dialog({
        component: PublicCrewRegistration,
        componentProps: {
            title: ts('publicCrewReg'),
            save: ts('register'),
        },
    })
        .onOk((payload) => {
            const publicCrew = useRepo(EventCrew).where('isPublic', true).whereHas('eventBoat', (query) => {
                query.where('eventId', event.value.id)
            }).orderBy('nonMemberRegId', 'desc').first()

            const licNo = publicCrew === null ? 1 : parseInt(nvl(publicCrew.nonMemberRegId?.substring(5), '0')) + 1

            useRepo(EventCrew).save({
                eventId: event.value.id,
                eventBoatId: eventBoat.id,
                nonMemberRegId: sprintf('8888-%04d', licNo),
                nonMemberFirstName: payload.firstName,
                nonMemberLastName: payload.lastName,
                nonMemberSex: payload.sex,
                nonMemberBirthDate: payload.birthDate,
                nonMemberEmail: payload.email,
                nonMemberPhone: payload.phone,
                captain: eventBoat.crew.length === 0,
                order: eventBoat.crew.length === 0 ? 1 : Math.max(...eventBoat.crew.map(crew => crew.order)) + 1,
            })

            event.value?.setModified()
        })
}

function addNewOneTimeCrew(eventBoat) {
    quasar.dialog({
        component: OneTimeCrewRegistration,
        componentProps: {
            title: ts('oneTimeCrewReg'),
            save: ts('register'),
        },
    })
        .onOk((payload) => {
            const publicCrew = useRepo(EventCrew).where('isOneTime', true).whereHas('eventBoat', (query) => {
                query.where('eventId', event.value.id)
            }).orderBy('nonMemberRegId', 'desc').first()

            const licNo = publicCrew === null ? 1 : parseInt(nvl(publicCrew.nonMemberRegId?.substring(5), '0')) + 1

            const birthCode = payload.citizenship === Country.CZ ? rodnecislo(payload.birthCode) : null

            useRepo(EventCrew).save({
                eventId: event.value.id,
                eventBoatId: eventBoat.id,
                nonMemberRegId: sprintf('9999-%04d', licNo),
                nonMemberFirstName: payload.firstName,
                nonMemberLastName: payload.lastName,
                nonMemberSex: (payload.citizenship === Country.CZ && birthCode !== null)
                    ? (birthCode.isFemale() ? 'female' : 'male')
                    : payload.sex,
                nonMemberBirthDate: (payload.citizenship === Country.CZ && birthCode !== null)
                    ? birthCode.birthDate()
                    : payload.birthDate,
                nonMemberBirthCode: payload.citizenship === Country.CZ ? String(payload.birthCode) : null,
                nonMemberCitizenship: payload.citizenship,
                nonMemberStreet: payload.street,
                nonMemberCity: payload.city,
                nonMemberZip: payload.zip?.replace(/\s/g, ''),
                nonMemberHouseNumber: payload.houseNumber?.replace(/\s/g, ''),
                nonMemberOrientationNumber: payload.orientationNumber?.replace(/\s/g, ''),
                nonMemberRegistrationNumber: payload.registrationNumber?.replace(/\s/g, ''),
                nonMemberEmail: payload.email,
                nonMemberPhone: payload.phone,
                captain: eventBoat.crew.length === 0,
                order: eventBoat.crew.length === 0 ? 1 : Math.max(...eventBoat.crew.map(crew => crew.order)) + 1,
            })

            event.value?.setModified()
        })
}

function addNewForeignCrew(eventBoat) {
    quasar.dialog({
        component: ForeignCrewRegistration,
        componentProps: {
            title: ts('foreignCrewReg'),
            save: ts('register'),
        },
    })
        .onOk((payload) => {
            const publicCrew = useRepo(EventCrew).where('isForeign', true).whereHas('eventBoat', (query) => {
                query.where('eventId', event.value.id)
            }).where('nonMemberRegId', (value) => {
                return parseInt(value.substring(0, 4)) === payload.country
            }).orderBy('nonMemberRegId', 'desc').first()

            const licNo = publicCrew === null ? 1 : parseInt(nvl(publicCrew.nonMemberRegId?.substring(5), '0')) + 1

            useRepo(EventCrew).save({
                eventId: event.value.id,
                eventBoatId: eventBoat.id,
                nonMemberRegId: sprintf('%04d-%04d', payload.country, licNo),
                nonMemberFirstName: payload.firstName,
                nonMemberLastName: payload.lastName,
                nonMemberSex: payload.sex,
                nonMemberBirthDate: payload.birthDate,
                nonMemberEmail: payload.email,
                nonMemberPhone: payload.phone,
                captain: eventBoat.crew.length === 0,
                order: eventBoat.crew.length === 0 ? 1 : Math.max(...eventBoat.crew.map(crew => crew.order)) + 1,
            })

            event.value?.setModified()
        })
}

async function printStartList(onlyCaptains = false, asPresenceList = false) {
    let counter = 0

    const reportLocale = event.value.forceEnglishReports ? 'en' : localization.global.locale.value

    const eventClass = event.value.classAttended(selClass.value)
    const useDesc = event.value.descInResultsTitle !== null && event.value.descInResultsTitle !== undefined
        && event.value.descInResultsTitle.length !== 0
    const useCrewDesc = event.value.crewDescInResultsTitle !== null && event.value.crewDescInResultsTitle !== undefined
        && event.value.crewDescInResultsTitle.length !== 0

    let factor = 0

    if (useCrewDesc)
        ++factor

    if (useDesc)
        ++factor

    if (eventClass.boatClass.useBoatNames && isBoatNamesUsed.value)
        ++factor

    if (isHandicapUsed.value)
        ++factor

    if (wsIdRequired.value)
        ++factor

    const paymentInfo = {
        '-1': ts('unknownPayment', {}, { locale: reportLocale }),
        '0': ts('noPayment', {}, { locale: reportLocale }),
        '1': ts('cardPayment', {}, { locale: reportLocale }),
        '2': ts('coinPayment', {}, { locale: reportLocale }),
    }

    const boatRows = asPresenceList
        ? (attendedBoats.value as EventBoat[])
            .map<number>((boat: EventBoat) => (onlyCaptains ? 1 : boat.crew.length))
            .reduce<number[]>((acc: number[], n: number) => (acc.push((acc.at(-1) ?? 1) + n), acc), [1])
        : []

    const document = await buildPrintMeta({
        event: event.value,
        title: ts(asPresenceList ? 'presenceList' : 'crewList', {}, { locale: reportLocale }),
        pageOrientation: (factor >= 2 || asPresenceList) ? 'landscape' : 'portrait',
        locale: reportLocale,
    })

    const content = {
        ...document,
        content: [
            buildStandardHeader(event.value, { locale: reportLocale }),
            {
                marginTop: 8,
                marginBottom: 8,
                table: {
                    headerRows: 0,
                    widths: ['*', '*'],
                    body: [
                        [
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 4,
                                text: [
                                    {
                                        text: ts('boatClassShortcut', {}, { locale: reportLocale }) + ':  ',
                                        style: 'em',
                                    },
                                    {
                                        text: eventClass.eventClassName,
                                        style: 'em',
                                    },
                                ],
                            },
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginRight: 4,
                                alignment: 'right',
                                text: [
                                    {
                                        text: ts('plannedCoefficient', {}, { locale: reportLocale }) + ':  ',
                                        style: 'em',
                                    },
                                    { text: eventClass.weight, style: 'em' },
                                ],
                            },
                        ],
                    ],
                },
                layout: {
                    vLineWidth: (i, node) => ((i === 0 || i === node.table.widths.length) ? 1 : 0),
                    hLineWidth: (i, node) => ((i === 0 || i === node.table.body.length) ? 1 : 0),
                    hLineColor: 'black',
                    vLineColor: 'black',
                },
            },
            {
                table: {
                    headerRows: 1,
                    widths: [
                        'auto',
                        'auto',
                        'auto',
                        '*',
                        'auto',
                        ...(wsIdRequired.value ? ['auto'] : []),
                        'auto',
                        'auto',
                        (useCrewDesc || useDesc || (eventClass.boatClass.useBoatNames && isBoatNamesUsed.value)
                                || isHandicapUsed.value || wsIdRequired.value || asPresenceList)
                            ? 'auto'
                            : '*',
                        ...(isHandicapUsed.value ? ['*'] : []),
                        ...(eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? ['*'] : []),
                        ...(useCrewDesc ? ['*'] : []),
                        ...(useDesc ? ['*'] : []),
                        ...(asPresenceList ? ['auto'] : []),
                        '*',
                        ...(asPresenceList ? ['*'] : []),
                        ...(asPresenceList ? ['*'] : []),
                    ],
                    body: [
                        [
                            { text: ts('orderShortcut', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('sailNo', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('lastName', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('name', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('regId', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            ...(wsIdRequired.value
                                ? [
                                    { text: ts('wsId', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                                ]
                                : []),
                            { text: ts('categoryShortcut', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('bornShortcut', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            { text: ts('sexShort', {}, { locale: reportLocale }).toLowerCase(), style: 'em' },
                            ...(isHandicapUsed.value
                                ? [{
                                    text: ts('handicap', {}, { locale: reportLocale }).toLowerCaseFirst(),
                                    style: 'em',
                                }]
                                : []),
                            ...(eventClass.boatClass.useBoatNames && isBoatNamesUsed.value
                                ? [{
                                    text: ts('boatName', {}, { locale: reportLocale }).toLowerCase(),
                                    style: 'em',
                                }]
                                : []),
                            ...(useCrewDesc ? [{ text: event.value.crewDescInResultsTitle, style: 'em' }] : []),
                            ...(useDesc ? [{ text: event.value.descInResultsTitle, style: 'em' }] : []),
                            ...(asPresenceList
                                ? [{ text: ts('payment', {}, { locale: reportLocale }), style: 'em' }]
                                : []),
                            { text: ts('club', {}, { locale: reportLocale }).toLowerCaseFirst(), style: 'em' },
                            ...(asPresenceList
                                ? [{ text: ts('desc', {}, { locale: reportLocale }).toLowerCaseFirst(), style: 'em' }]
                                : []),
                            ...(asPresenceList
                                ? [{ text: ts('sign', {}, { locale: reportLocale }), style: 'em' }]
                                : []),
                        ],
                        ...(attendedBoats.value.map(function(boat) {
                            let template = [
                                { text: sprintf('%d.', ++counter), alignment: 'right' },
                                boat.sailNo,
                                '',
                                '',
                                '',
                                ...(wsIdRequired.value ? [''] : []),
                                {
                                    text: boat.orderedCategories.map(category => category.visibleShortcut).join(', '),
                                    style: 'smaller',
                                },
                                '',
                                '',
                                ...(isHandicapUsed.value
                                    ? [{
                                        text: boat?.handicapShortcut,
                                        style: 'smaller',
                                    }]
                                    : []),
                                ...(eventClass.boatClass.useBoatNames && isBoatNamesUsed.value
                                    ? [{
                                        text: nvl(boat?.name, '').shorten(22),
                                        style: 'smaller',
                                    }]
                                    : []),
                                ...(useCrewDesc ? [{ text: '', style: 'smaller' }] : []),
                                ...(useDesc ? [{ text: boat?.detail ?? '', style: 'smaller' }] : []),
                                ...(asPresenceList ? [{ text: paymentInfo[String(boat?.paid ?? '-1')] }] : []),
                                '',
                                ...(asPresenceList ? [''] : []),
                                ...(asPresenceList ? [{ text: '', style: 'sign' }] : []),
                            ]

                            if (boat.crew === undefined || boat.crew === null || boat.crew.length === 0)
                                return [template]

                            const crew = onlyCaptains ? [boat.crew[0]] : boat.crew

                            return crew.map(function(crew) {
                                let line = template
                                template = [
                                    '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    ...(wsIdRequired.value ? [''] : []),
                                    '',
                                    '',
                                    '',
                                    ...(isHandicapUsed.value ? [''] : []),
                                    ...(eventClass.boatClass?.useBoatNames && isBoatNamesUsed.value ? [''] : []),
                                    ...(useCrewDesc ? [''] : []),
                                    ...(useDesc ? [''] : []),
                                    ...(asPresenceList ? [''] : []),
                                    '',
                                    ...(asPresenceList ? [''] : []),
                                    ...(asPresenceList ? [{ text: '', style: 'sign' }] : []),
                                ]

                                line[2] = crew.lastName?.shorten(30) ?? ''
                                line[3] = crew.firstName?.shorten(30) ?? ''
                                line[4] = crew.regId

                                let col = 5

                                if (wsIdRequired.value)
                                    line[col++] = crew.wsId ?? ''

                                ++col

                                line[col++] = crew.birthYear === undefined
                                    ? ''
                                    : {
                                        text: [
                                            { text: crew.birthYear },
                                            ...(crew.agedBirthYear != null ? [{ text: '*', sup: true }] : []),
                                        ],
                                    }
                                line[col++] = crew.sex === 'male'
                                    ? ts('maleShortcut', {}, {}, reportLocale)
                                    : ts('femaleShortcut', {}, {}, reportLocale)

                                if (isHandicapUsed.value)
                                    ++col

                                if (eventClass.boatClass.useBoatNames && isBoatNamesUsed.value)
                                    ++col

                                if (useCrewDesc)
                                    line[col++] = { text: crew.note, style: 'smaller' }

                                if (useDesc)
                                    ++col

                                if (asPresenceList)
                                    ++col

                                line[col] = {
                                    text: crew.visibleClub?.shorten(22) ?? '',
                                    style: (isBoatNamesUsed.value || asPresenceList) ? 'smaller' : '',
                                }

                                return line
                            })
                        }).flat(1)),
                    ],
                },
                layout: {
                    vLineWidth: () => (0),
                    hLineWidth: (i, node) => (i === node.table.headerRows || i === node.table.body.length
                        ? 1
                        : (boatRows.includes(i))
                        ? .5
                        : 0),
                    vLineColor: 'black',
                    hLineColor: (
                        i,
                        node,
                    ) => ((i === node.table.headerRows || i === node.table.body.length) ? 'black' : '#ccc'),
                    paddingTop: (i, node) => ((i >= node.table.headerRows && asPresenceList) ? 10 : 0),
                    paddingBottom: (i, node) => ((i >= node.table.headerRows && asPresenceList) ? 10 : 0),
                },
                style: 'crewlist',
            },
            ...(eventClass.orderedCategories.length > 0
                ? [
                    {
                        headerRows: 1,
                        marginTop: 16,
                        table: {
                            widths: ['auto', 'auto', '*'],
                            body: [
                                [
                                    { text: ts('shortcut', {}, {}, reportLocale), style: 'em' },
                                    { text: ts('category', {}, {}, reportLocale), style: 'em' },
                                    { text: ts('conditions', {}, {}, reportLocale), style: 'em' },
                                ],
                                ...eventClass.orderedCategories.map(
                                    category => [category.visibleShortcut, category.visibleTitle, category.explanation],
                                ),
                            ],
                        },
                        layout: {
                            vLineWidth: () => (0),
                            hLineWidth: () => (0),
                        },
                        style: 'legend',
                    },
                ]
                : []),
        ],
    }

    pdfMake.createPdf(content).download(
        'ctl-' + event.value.ctlId + '-'
            + (asPresenceList ? 'checkin' : 'entry') + '-list-' + eventClass.boatClass.shortcut.toLowerCase() + '.pdf',
    )
}

function transformCrewFromServer(crew, counter) {
    const regId = crew.memberId.replace('-', '')
    const club = parseInt(regId.substring(0, 4))

    if (club <= 8000)
        return {
            memberId: regId,
            captain: crew.isCaptain === 1,
            order: counter,
            nonMemberRegId: crew.memberId,
            nonMemberFirstName: crew.firstName,
            nonMemberLastName: crew.lastName,
            nonMemberEmail: crew.email,
            nonMemberPhone: crew.phone,
            nonMemberSex: crew.sex,
            nonMemberBirthDate: crew.birthDate,
        }

    if (club === 8888)
        return {
            nonMemberRegId: crew.memberId,
            nonMemberFirstName: crew.firstName,
            nonMemberLastName: crew.lastName,
            captain: crew.isCaptain === 1,
            nonMemberSex: crew.sex,
            nonMemberBirthDate: crew.birthDate,
            nonMemberEmail: crew.email,
            nonMemberPhone: crew.phone,
            order: counter,
        }

    if (club === 9999)
        return {
            nonMemberRegId: crew.memberId,
            nonMemberFirstName: crew.firstName,
            nonMemberLastName: crew.lastName,
            captain: crew.isCaptain === 1,
            nonMemberSex: crew.sex,
            nonMemberBirthDate: crew.birthDate,
            nonMemberBirthCode: crew.nationality === Country.CZ ? String(crew.birthCode) : null,
            nonMemberCitizenship: crew.nationality,
            nonMemberAddress: crew.nationality === Country.CZ ? null : crew.address,
            nonMemberEmail: crew.email,
            nonMemberPhone: crew.phone,
            order: counter,
        }

    if (club >= 9901)
        return {
            nonMemberRegId: crew.memberId,
            nonMemberFirstName: crew.firstName,
            nonMemberLastName: crew.lastName,
            captain: crew.isCaptain === 1,
            nonMemberSex: crew.sex,
            nonMemberBirthDate: crew.birthDate,
            nonMemberEmail: crew.email,
            nonMemberPhone: crew.phone,
            order: counter,
        }

    return null
}

enum EditMode {
    Display,
    BatchHandicapSetup,
}

const editMode = ref(EditMode.Display)

function batchHandicapSetup() {
    editMode.value = EditMode.BatchHandicapSetup
}

function updateFromServer() {
    let currentClass = selClass.value === '-all' ? null : selClass.value

    if (currentClass !== null && !isPositiveInteger(currentClass))
        currentClass = decodeBoatClass(currentClass)

    apiRequest(useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken))
        .get('/events/' + event.value.ctlId + '/boats')
        .then((response) => {
            if (response.status === 200) {
                const current = currentClass === null
                    ? useRepo(EventBoat).where('eventId', event.value.id).withAllRecursive().get()
                    : useRepo(EventBoat).where('eventId', event.value.id).where('boatClassId', currentClass)
                        .withAllRecursive().get()

                const currentList = current.map(boat =>
                    String(Number(boat.boatClassId)) + '|' + boat.country + '|' + boat.sailNumber
                )
                const newData = currentClass === null
                    ? response.data
                    : response.data.filter((boat) => decodeBoatClass(boat.classId) === currentClass)
                const changeList = []
                const marked = []

                let iterator = 0

                newData.forEach((boat) => {
                    const boatIdent = decodeBoatClass(boat.classId) + '|' + boat.country + '|' + boat.sailNumber

                    marked.push(boatIdent)

                    if (currentList.includes(boatIdent)) {
                        const crewList = []
                        let counter = 0

                        const origBoat = current.map(b => ({
                            boatClassId: b.boatClassId,
                            country: b.country,
                            sailNumber: b.sailNumber,
                            id: b.id,
                            paid: b.paid,
                            crewHash: b.crew.map(c => crewHash(c.dump)).join('|'),
                        }))
                            .filter(b =>
                                b.boatClassId === decodeBoatClass(boat.classId)
                                && b.country === boat.country
                                && b.sailNumber === boat.sailNumber
                            )[0]

                        const newCrewHash = boat.crew.map((crew) => {
                            const newCrew = transformCrewFromServer(crew, ++counter)

                            if (newCrew === null)
                                return undefined

                            const hash = crewHash(crewDump(newCrew))

                            crewList.push({ ...newCrew, eventId: event.value.id })

                            return hash
                        }).join('|')

                        if (
                            origBoat.crewHash === newCrewHash
                            && (origBoat.paid === 2 || (origBoat.paid > 0) === boat.paid)
                        )
                            return

                        if (origBoat.crewHash === newCrewHash) {
                            changeList.push({
                                operation: 'paid',
                                id: ++iterator,
                                boat: {
                                    id: origBoat.id,
                                    eventId: event.value.id,
                                    boatClassId: decodeBoatClass(boat.classId),
                                    country: boat.country,
                                    sailNumber: String(boat.sailNumber),
                                    paid: !boat.paid ? 0 : (origBoat.paid > 0 ? origBoat.paid : 1),
                                    crew: origBoat.crewHash.split('|').map(hash => destr(hash)),
                                },
                            })

                            return
                        }

                        changeList.push({
                            operation: 'update',
                            id: ++iterator,
                            original: {
                                boatId: origBoat.id,
                                boatClassId: decodeBoatClass(boat.classId),
                                country: boat.country,
                                sailNumber: String(boat.sailNumber),
                                crew: origBoat.crewHash.split('|').map(hash => destr(hash)),
                            },
                            boat: {
                                id: origBoat.id,
                                eventId: event.value.id,
                                boatClassId: decodeBoatClass(boat.classId),
                                country: boat.country,
                                sailNumber: String(boat.sailNumber),
                                paid: !boat.paid ? 0 : (origBoat.paid > 0 ? origBoat.paid : 1),
                                crew: crewList,
                            },
                        })
                    } else {
                        const crewList = []
                        let counter = 0

                        boat.crew.forEach((crew) => {
                            const newCrew = transformCrewFromServer(crew, ++counter)

                            if (newCrew !== null)
                                crewList.push({ ...newCrew, eventId: event.value.id })
                        })

                        changeList.push({
                            operation: 'add',
                            id: ++iterator,
                            boat: {
                                eventId: event.value.id,
                                boatClassId: decodeBoatClass(boat.classId),
                                country: boat.country,
                                sailNumber: String(boat.sailNumber),
                                paid: boat.paid ? 1 : 0,
                                crew: crewList,
                            },
                        })
                    }
                })

                currentList.filter(b => !marked.includes(b)).forEach((boatIdent) => {
                    const parts = boatIdent.split('|')

                    const origBoat = current.find(b =>
                        b.boatClassId === parseInt(parts[0])
                        && b.country === parts[1] && b.sailNumber === parts[2]
                    )

                    changeList.push({
                        operation: 'remove',
                        id: ++iterator,
                        original: {
                            boatId: origBoat.id,
                            boatClassId: origBoat.boatClassId,
                            country: origBoat.country,
                            sailNumber: origBoat.sailNumber,
                            crew: origBoat.crew,
                        },
                    })
                })

                const operOrder = {
                    add: 1,
                    paid: 2,
                    update: 3,
                    remove: 4,
                }

                if (changeList.length === 0)
                    quasar.dialog({
                        component: InfoBox,
                        componentProps: {
                            message: ts('noDataForUpdate'),
                        },
                    })
                else {
                    quasar.dialog({
                        component: UpdateRegistrations,
                        componentProps: {
                            event: event.value,
                            changeList: changeList.sort((a, b) =>
                                a.operation === b.operation
                                    ? (a.operation === 'remove'
                                        ? (a.original.country === b.original.country
                                            ? a.original.sailNumber - b.original.sailNumber
                                            : a.original.country.localeCompare(b.original.country))
                                        : (a.boat.country === b.boat.country
                                            ? a.boat.sailNumber - b.boat.sailNumber
                                            : a.boat.country.localeCompare(b.boat.country)))
                                    : operOrder[a.operation] - operOrder[b.operation]
                            ),
                        },
                    })
                        .onOk((payload) => {
                            payload.changeList.forEach((change) => {
                                if (change.operation === 'add') {
                                    useRepo(EventBoat).save(change.boat)
                                } else if (change.operation === 'remove') {
                                    useRepo(EventCrew).where('eventBoatId', change.original.boatId).delete()
                                    useRepo(EventResult).where('eventBoatId', change.original.boatId).delete()
                                    useRepo(EventBoat).destroy(change.original.boatId)
                                } else if (change.operation === 'paid') {
                                    useRepo(EventBoat).save({
                                        id: change.boat.id,
                                        paid: change.boat.paid,
                                    })
                                } else {
                                    useRepo(EventCrew).where('eventBoatId', change.original.boatId).delete()
                                    useRepo(EventBoat).save(change.boat)
                                }
                            })

                            event.value?.setModified()
                        })
                }
            }
        })
}

function saveShowOnlyCaptains(value) {
    useRepo(EventsRepo).save({ id: event.value.id, showOnlyCaptains: value })
}

function makePublic(boatCrew) {
    const publicCrew = useRepo(EventCrew).where('isPublic', true).whereHas('eventBoat', (query) => {
        query.where('eventId', event.value.id)
    }).orderBy('nonMemberRegId', 'desc').first()

    const licNo = publicCrew === null ? 1 : parseInt(nvl(publicCrew.nonMemberRegId?.substring(5), '0')) + 1

    useRepo(EventCrew).save({
        id: boatCrew.id,
        nonMemberRegId: sprintf('8888-%04d', licNo),
    })

    event.value?.setModified()
}

function feePaid(boat) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'warning',
            question: ts('isFeePaid', {
                sailNo: boat.sailNo,
                boatClass: boat.eventClassName,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventBoat).save({ id: boat.id, paid: 2 })
            event.value?.setModified()
        })
}

function sendToRrs() {
    // according to doc https://www.racingrulesofsailing.org/apipie/1.0/competitors.en.html

    const payload = {
        uuid: event.value.rrsUuid,
        source: 'CZEScoring',
        competitors: attendedBoats.value.map(function(boat) {
            return boat.crew.filter(crew => crew.captain).map(function(crew) {
                return {
                    competitor_id: crew.regId?.replace('-', '') ?? '',
                    boat_class: boat.eventClass?.eventClassName ?? '',
                    boat_name: boat.name ?? '',
                    country_code: boat.country ?? '',
                    sail_number: boat.sailNumber ?? '',
                    first_name: crew.firstName ?? '',
                    last_name: crew.lastName ?? '',
                    mna_code: crew.isForeign ? crew.visibleClub.substring(0, 3) ?? '' : 'CZE',
                    mna_number: crew.regId?.replace('-', '') ?? '',
                    email: crew.email ?? '',
                    phone: crew.phone?.formatPhoneStr() ?? '',
                    club_name: crew.visibleClub ?? '',
                }
            })
        }).flat(1),
    }

    const rrsClient = axios.create({
        baseURL: 'https://www.racingrulesofsailing.org/',
        timeout: 5000,
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
        },
    })

    rrsClient.post('/api/competitors', payload).then((response) => {
        if (response.status === 200) {
            quasar.notify({
                icon: 'sailing',
                color: 'positive',
                message: ts('rrsUploadFinished'),
                timeout: 3000,
            })
        } else {
            quasar.notify({
                icon: 'error',
                color: 'negative',
                message: ts('cannotUpdateRrs'),
                timeout: 10000,
            })
        }
    }).catch(() => {
        quasar.notify({
            icon: 'error',
            color: 'negative',
            message: ts('cannotUpdateRrs'),
            timeout: 10000,
        })
    })
}

function updateBoatHandicap(eventBoat, handicap, originalHandicap) {
    if (handicap === originalHandicap)
        return

    if (eventBoat?.eventClass?.isHandicapSelectable)
        useRepo(EventBoat).save({ id: eventBoat.id, handicapSelected: handicap })
    else
        useRepo(EventBoat).save({
            id: eventBoat.id,
            handicapValue: eventBoat?.eventClass?.handicapSystem === 'zadar'
                ? parseFloat(handicap)
                : parseInt(handicap),
        })

    event.value?.setModified()
}

function updateRaceHandicap(eventBoat, eventRaceId, handicap, originalHandicap) {
    const eventRaceIdCorrected = eventRaceId === undefined ? null : eventRaceId

    if (handicap === originalHandicap)
        return

    const noChangeLabel = '-- ' + ts('noChange') + ' --'

    if (handicap === '--' || handicap === noChangeLabel) {
        const raceRepo = useRepo(EventBoatRace)
        const existing = raceRepo.where('eventBoatId', eventBoat.id).where('eventRaceId', eventRaceIdCorrected).first()

        if (existing) {
            raceRepo.destroy(existing.id)

            if (eventBoat.eventBoatRaces) {
                const index = eventBoat.eventBoatRaces.findIndex(r => r.id === existing.id)

                if (index !== -1) {
                    eventBoat.eventBoatRaces.splice(index, 1)
                }
            }

            event.value?.setModified()
        }
        return
    }

    const raceRepo = useRepo(EventBoatRace)
    const existing = raceRepo.where('eventBoatId', eventBoat.id).where('eventRaceId', eventRaceIdCorrected).first()

    const handicapSystem = eventBoat.eventClass?.handicapSystem
    const isSelectable = eventBoat.eventClass?.isHandicapSelectable

    const data = {
        eventBoatId: eventBoat.id,
        eventRaceId: eventRaceIdCorrected,
        handicapSelected: isSelectable ? String(handicap) : null,
        handicapValue: !isSelectable ? (handicapSystem === 'zadar' ? parseFloat(handicap) : parseInt(handicap)) : null,
    }

    if (existing) {
        raceRepo.save({ ...data, id: existing.id })
    } else {
        const stored = raceRepo.save(data)

        if (!eventBoat.eventBoatRaces) {
            eventBoat.eventBoatRaces = []
        }

        eventBoat.eventBoatRaces.push(Array.isArray(stored) ? stored[0] : stored)
    }

    event.value?.setModified()

    if (eventRaceIdCorrected === null) {
        // Force refresh for Rn+1
        useRepo(EventBoat).save({ id: eventBoat.id })
    }
}

/**
 * wsId problem and XRR:
 * (1) convert all values from boat desc to wsId field (RS Tera)
 * (2) add XML XRR export capability
 */
function doMagic() {
    useRepo(EventBoat).where('eventId', event.value.id).get().forEach((boat) => {
        useRepo(EventCrew).where('eventBoatId', boat.id).get().forEach((crew) => {
            useRepo(EventCrew).save({ id: crew.id, note: null }) // wsId: crew.note,
        })
    })
}
</script>

<style scoped lang="sass">
.boat-class-column
    width: 10em

.boat-country-column
    width: 6em

.boat-sailno-column
    width: 10em

tr.invalid > td
    color: $negative
    font-weight: bold

.minicrewlist
    font-size: 80%

.captain
    font-weight: bold

.aged-birth-year-marker
    color: $negative

.current-boat, .current-boat div
    background-color: #ffffe7

.filter-bar
    font-size: 80%

.invisible-button
    visibility: hidden

tr.license-issue > td
    color: $negative
    font-weight: bold
</style>
